// Public "Take a Peek" demo tour. Fully self-contained: every number on
// screen comes from ./demoData, so the tour can never reach a real
// tenant. All controls are decorative and non-interactive by design.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, ArrowRight, Lock, LayoutDashboard, CalendarDays, ClipboardCheck,
  Gauge, TrendingUp, Smartphone, Search, Bell, ChevronDown,
} from 'lucide-react';
import { DemoDashboard, DemoGrid, DemoPresence, DemoWorkload, DemoImprove, DemoFlowCrew } from './DemoScreens';
import { DEMO_COMPANY } from './demoData';

const SCREENS = [
  { key: 'dashboard', label: 'Dashboard',        icon: LayoutDashboard, caption: 'The week at a glance — demand, coverage, and what needs a decision today.', render: DemoDashboard },
  { key: 'grid',      label: 'Week planning',    icon: CalendarDays,    caption: 'One grid per department. Drag a person onto a shift, publish, done.',        render: DemoGrid },
  { key: 'presence',  label: 'Presence',         icon: ClipboardCheck,  caption: 'What actually happened on the floor, next to what was planned.',            render: DemoPresence },
  { key: 'workload',  label: 'Workload & balance', icon: Gauge,         caption: 'Volume in, required hours out — then balance it against real capacity.',    render: DemoWorkload },
  { key: 'improve',   label: 'KPI & improvement', icon: TrendingUp,     caption: 'The gap between plan and reality becomes a ranked action list.',            render: DemoImprove },
  { key: 'flowcrew',  label: 'FlowCrew app',     icon: Smartphone,      caption: 'The crew\'s half: their schedule, their team, their language.',             render: DemoFlowCrew },
];

const SIDEBAR = ['Dashboard', 'Projects', 'Workfloor', 'Teams', 'Planning', 'Archive', 'Messages'];

export default function DemoTour() {
  const [idx, setIdx] = useState(0);
  const screen = SCREENS[idx];
  const Screen = screen.render;

  return (
    <div className="lp-scope min-h-screen bg-[#070d18]" data-testid="demo-tour-page">
      {/* Demo ribbon */}
      <div className="sticky top-0 z-30 border-b border-cyan-400/20 bg-[#070d18]/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
          <Link to="/" className="group inline-flex items-center gap-2 text-sm text-white/55 transition-colors hover:text-white" data-testid="demo-back-home">
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            Back to home
          </Link>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-200" data-testid="demo-readonly-badge">
            <Lock className="h-3 w-3" />
            Demo — read-only
          </span>
          <span className="hidden text-xs text-white/35 sm:inline">
            Fictional data · nothing here is a live customer environment
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" className="h-9 text-white/60 hover:text-white" asChild data-testid="demo-login-link">
              <Link to="/login">Sign in</Link>
            </Button>
            <Button className="h-9 bg-cyan-500 font-semibold text-[#04141a] hover:bg-cyan-400" asChild data-testid="demo-cta-trial">
              <Link to="/signup">Start free trial</Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6">
        <div className="mb-5">
          <h1 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl" data-testid="demo-screen-title">
            {screen.label}
          </h1>
          <p className="mt-1.5 max-w-2xl text-sm text-white/50">{screen.caption}</p>
        </div>

        {/* Screen rail */}
        <div className="mb-5 flex gap-2 overflow-x-auto pb-1" data-testid="demo-screen-rail">
          {SCREENS.map((s, i) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setIdx(i)}
              data-testid={`demo-rail-${s.key}`}
              className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-medium transition-colors ${
                i === idx
                  ? 'border-cyan-400/50 bg-cyan-400/15 text-cyan-100'
                  : 'border-white/10 bg-white/[0.03] text-white/50 hover:border-white/20 hover:text-white/80'
              }`}
            >
              <s.icon className="h-3.5 w-3.5" />
              {s.label}
            </button>
          ))}
        </div>

        {/* Fake app window */}
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-white shadow-[0_40px_120px_-40px_rgba(0,0,0,0.9)]">
          <div className="flex">
            {/* Sidebar */}
            <aside className="hidden w-52 shrink-0 border-r border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 lg:block">
              <img src="/brand/kanriva-logo.png" alt="Kanriva™" className="mb-6 h-6 w-auto" />
              <nav className="space-y-1">
                {SIDEBAR.map((s) => (
                  <div
                    key={s}
                    className={`flex items-center justify-between rounded-lg px-3 py-2 text-xs font-medium ${
                      s === 'Planning' ? 'bg-slate-900 text-white' : 'text-slate-500'
                    }`}
                  >
                    {s}
                    {s !== 'Planning' && <ChevronDown className="h-3 w-3 opacity-40" />}
                  </div>
                ))}
              </nav>
              <div className="mt-8 rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-[10px] uppercase tracking-wider text-slate-400">Tenant</p>
                <p className="mt-1 text-xs font-semibold text-slate-700">{DEMO_COMPANY}</p>
                <p className="text-[10px] text-slate-400">demo · fictional</p>
              </div>
            </aside>

            {/* Content */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3">
                <div className="flex h-8 flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 text-xs text-slate-400">
                  <Search className="h-3.5 w-3.5" />
                  Search workers, shifts, departments…
                </div>
                <Bell className="h-4 w-4 text-slate-400" />
                <span className="hidden h-7 items-center rounded-full bg-slate-900 px-2.5 text-[11px] font-medium text-white sm:inline-flex">
                  Demo planner
                </span>
              </div>

              {/* pointer-events-none: the tour is deliberately passive */}
              <div className="pointer-events-none select-none bg-slate-50/60 p-4 sm:p-6" aria-hidden="false">
                <Screen />
              </div>
            </div>
          </div>
        </div>

        {/* Tour navigation */}
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <Button
            variant="outline"
            className="border-white/15 bg-transparent text-white hover:bg-white/5"
            disabled={idx === 0}
            onClick={() => setIdx((i) => Math.max(0, i - 1))}
            data-testid="demo-prev"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Previous
          </Button>
          <span className="text-xs text-white/35" data-testid="demo-progress">
            {idx + 1} / {SCREENS.length}
          </span>
          {idx === SCREENS.length - 1 ? (
            <Button className="bg-cyan-500 font-semibold text-[#04141a] hover:bg-cyan-400" asChild data-testid="demo-next">
              <Link to="/signup">Start free trial <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          ) : (
            <Button
              className="bg-cyan-500 font-semibold text-[#04141a] hover:bg-cyan-400"
              onClick={() => setIdx((i) => Math.min(SCREENS.length - 1, i + 1))}
              data-testid="demo-next"
            >
              Next <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>

        <p className="mt-6 text-center text-[11px] text-white/25">
          Kanriva™ is a trademark of Kaizen Kanri. Every name and number in this tour is invented.
        </p>
      </div>
    </div>
  );
}
